import { LinkIcon, LocationIcon as LocationSvg } from '@/assets/icons'
import Modal from '@components/Modal'
import { normalizeExternalUrl } from '@lib/utils'
import { GetIdeaResponse } from '@orbit/api-types'
import React from 'react'
import {
	LocationIcon,
	LocationPrimary,
	LocationSecondary,
	LocationSection,
	LocationText,
	MapButton,
	MetaSection,
	NotesContent,
	SectionLabel,
} from '../Accommodations/AccommodationDetailPage/AccommodationDetailPage.styles'
import { getGoogleMapsUrl } from '../Accommodations/accommodationHelpers'
import {
	CardLinks,
	CardTags,
	LinkInfo,
	LinkPreviewCard,
	LinkSource,
	LinkThumbnail,
	LinkUrl,
	Tag,
} from '../Ideas/ideaLinks.styles'
import { ideaThumbnailUrl } from '../Ideas/ideaFilters'
import { BOOKMARK_CATEGORY_LABELS } from '../Ideas/utils'
import { CategoryBadge, DetailStack, HeroImage } from './PublicIdeaModal.styles'
import { appleMaps, danger, googleBlue, instagram, mutedGrey, textMuted, tiktok } from '@/theme/colors'
import { iconSizes } from '@/theme/tokens'

const getSourceColor = (source: string): string => {
	switch (source.toLowerCase()) {
		case 'tiktok':
			return tiktok
		case 'instagram':
			return instagram
		case 'youtube':
			return danger
		case 'googlemaps':
			return googleBlue
		case 'applemaps':
			return appleMaps
		default:
			return textMuted
	}
}

const formatSourceName = (source: string): string => {
	if (source === 'GoogleMaps') return 'Google Maps'
	if (source === 'AppleMaps') return 'Apple Maps'
	return source
}

interface PublicIdeaModalProps {
	/** The idea being viewed; `null` keeps the popup closed. */
	idea: GetIdeaResponse | null
	/** Creator's display name, or `null` to omit the meta line. */
	addedBy: string | null
	onClose: () => void
}

/**
 * Read-only view popup for one shared idea — the grid tile shows only the
 * thumbnail and name, so this carries everything else: category, location
 * (with map link), description, external link previews, tags, and who added
 * it. Same section language as the public accommodation detail page, minus
 * every edit affordance and minus attachments (not publicly resolvable —
 * ADR 0003).
 */
const PublicIdeaModal: React.FC<PublicIdeaModalProps> = ({ idea, addedBy, onClose }) => {
	if (!idea) return null

	const thumbnail = ideaThumbnailUrl(idea)
	const mapsUrl = getGoogleMapsUrl(idea.location)
	const links = idea.links ?? []
	const tags = idea.tags ?? []

	return (
		<Modal isOpen onClose={onClose} title={idea.name} size="medium">
			{thumbnail && <HeroImage src={thumbnail} alt={idea.name} />}
			<DetailStack>
				<CategoryBadge>{BOOKMARK_CATEGORY_LABELS[idea.category]}</CategoryBadge>

				{idea.location && (
					<LocationSection>
						<LocationIcon>
							<LocationSvg sx={{ fontSize: iconSizes.lg }} />
						</LocationIcon>
						<LocationText>
							<LocationPrimary>
								{idea.location.primaryText || idea.location.fullText}
							</LocationPrimary>
							{idea.location.secondaryText && (
								<LocationSecondary>{idea.location.secondaryText}</LocationSecondary>
							)}
						</LocationText>
						{mapsUrl && (
							<MapButton href={mapsUrl} target="_blank" rel="noopener noreferrer">
								View Map
							</MapButton>
						)}
					</LocationSection>
				)}

				{idea.description && (
					<div>
						<SectionLabel>Description</SectionLabel>
						<NotesContent>{idea.description}</NotesContent>
					</div>
				)}

				{links.length > 0 && (
					<div>
						<SectionLabel>Links ({links.length})</SectionLabel>
						<CardLinks>
							{links.map(link => (
								<LinkPreviewCard
									key={link.id}
									href={normalizeExternalUrl(link.url)}
									target="_blank"
									rel="noopener noreferrer"
									$clickable
								>
									<LinkThumbnail src={link.thumbnail || undefined}>
										{!link.thumbnail && <LinkIcon htmlColor={mutedGrey} />}
									</LinkThumbnail>
									<LinkInfo>
										<LinkSource $color={getSourceColor(link.source)}>
											{formatSourceName(link.source)}
										</LinkSource>
										<LinkUrl>{link.url}</LinkUrl>
									</LinkInfo>
								</LinkPreviewCard>
							))}
						</CardLinks>
					</div>
				)}

				{tags.length > 0 && (
					<div>
						<SectionLabel>Tags</SectionLabel>
						<CardTags>
							{tags.map(tag => (
								<Tag key={tag}>{tag}</Tag>
							))}
						</CardTags>
					</div>
				)}

				{addedBy && (
					<MetaSection>
						<span>Added by {addedBy}</span>
					</MetaSection>
				)}
			</DetailStack>
		</Modal>
	)
}

export default PublicIdeaModal
